/**
 * Etichette export CSV della prima tabella markdown in chat: dipendono dal messaggio
 * utente che ha innescato il turno (skill Meta vs agente Kipify vs generico).
 */

export type MarkdownTableCsvExportLabels = {
  buttonLabel: string;
  snackbarSuccess: string;
  filenamePrefix: string;
};

type MessageLike = { role: string; text?: string | null };

export function getPrecedingUserMessageText(
  messages: MessageLike[],
  assistantIndex: number,
): string | undefined {
  for (let i = assistantIndex - 1; i >= 0; i--) {
    const m = messages[i];
    if (m && m.role === 'User' && (m.text ?? '').trim()) {
      return String(m.text).trim();
    }
  }
  return undefined;
}

/** Skill `/kipify-advisor` (EN/IT) + alias legacy per messaggi già inviati. */
export function isKipifyAgentSkillUserMessage(text: string): boolean {
  const t = String(text || '').trim();
  if (/^run the kipify agent\b/i.test(t)) return true;
  if (/^avvia l['\u2019]?agente kipify\b/i.test(t)) return true;
  if (/^open the kipify advisor\b/i.test(t)) return true;
  if (/^avvia (il )?consigliere kipify\b/i.test(t)) return true;
  return false;
}

/** Skill `/facebook-creative-analysis` (prefisso stabile del submitText). */
export function isFacebookCreativeSkillUserMessage(text: string): boolean {
  const t = String(text || '').trim();
  return /^run a read-only meta\b/i.test(t);
}

/** Skill `/google-ads-search-term-analysis` (submitText in `chatSkills.ts`). */
export function isGoogleAdsSearchTermsAuditSkillUserMessage(
  text: string,
): boolean {
  const t = String(text || '').trim();
  return /^run a read-only google ads search terms audit\b/i.test(t);
}

/** Skill `/meta-google-ad-copy-winners` (submitText in `chatSkills.ts`). */
export function isMetaGoogleAdCopyWinnersSkillUserMessage(
  text: string,
): boolean {
  const t = String(text || '').trim();
  return /^run the kipify skill:\s*meta\s*\+\s*google ad copy\b/i.test(t);
}

/**
 * CSV dedicati dal tool `google_ads_negative_keyword_miner` — evita il doppio download
 * accanto a "Download table (CSV)" che esporta solo la prima tabella markdown.
 */
export function hasGoogleAdsSearchTermsAuditCsvAttachments(
  attachments: Array<{ filename?: string | null }> | null | undefined,
): boolean {
  return (
    Array.isArray(attachments) &&
    attachments.some(
      (a) =>
        typeof a?.filename === 'string' &&
        a.filename.startsWith('kipify_google_ads_audit_'),
    )
  );
}

export function shouldHideMarkdownTableCsvButton(
  precedingUserText: string | undefined,
  attachments: Array<{ filename?: string | null }> | null | undefined,
): boolean {
  if (
    precedingUserText &&
    isGoogleAdsSearchTermsAuditSkillUserMessage(precedingUserText)
  ) {
    return true;
  }
  if (
    precedingUserText &&
    isMetaGoogleAdCopyWinnersSkillUserMessage(precedingUserText)
  ) {
    return true;
  }
  return hasGoogleAdsSearchTermsAuditCsvAttachments(attachments);
}

export function getMarkdownTableCsvExportLabels(
  precedingUserText: string | undefined,
): MarkdownTableCsvExportLabels {
  if (precedingUserText && isKipifyAgentSkillUserMessage(precedingUserText)) {
    return {
      buttonLabel: 'Download report (CSV)',
      snackbarSuccess: 'Report exported as CSV',
      filenamePrefix: 'kipify-agent-report',
    };
  }
  if (
    precedingUserText &&
    isFacebookCreativeSkillUserMessage(precedingUserText)
  ) {
    return {
      buttonLabel: 'Download Facebook creative report (CSV)',
      snackbarSuccess: 'Facebook creative report exported as CSV',
      filenamePrefix: 'facebook-creative-report',
    };
  }
  return {
    buttonLabel: 'Download table (CSV)',
    snackbarSuccess: 'Table exported as CSV',
    filenamePrefix: 'chat-table',
  };
}
